import axios from "axios";
import { format } from "date-fns";
import Lottie from "lottie-react";
import moment from "moment";
import pica from "pica";
import React, { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import Masonry from "react-masonry-css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
} from "reactstrap";
import { Btn, H4 } from "../../../AbstractElements";
import { useDeviceType } from "../../../Hooks/useDeviceType";
import { useCountryPath } from "../../../Hooks/useCountryPath";
import { useAuth } from "../../../_helper/Auth/AuthProvider";
import { useFileUpload } from "../../../_helper/FileUpload/FileUploadProvider";
import { useProject } from "../../../_helper/Projects/ProjectProvider";
import * as sending from "../../../assets/images/lottie/sending.json";
import { formatFileSize, getFileIcon } from "../../../libs/utils";
import { ConcernSkeleton } from "../../Skeleton/Skeleton";
import FileUploadCard from "./FileUploadCard";
import FileUploadInput from "./FileUploadInput";
import "./style.scss";

const ConcernPage = () => {
  //CONTEXT
  const { theme, isDark, user, authToken } = useAuth();
  const { getAllFiles } = useFileUpload();
  const { t } = useTranslation();
  const history = useNavigate();
  const buildPath = useCountryPath();
  const isConcernPage = useLocation().pathname.includes("/pages/concern");
  const {
    parentId: parentId,
    projectId: projectId,
    assetId: assetId,
    category: category,
    assetName: assetName,
  } = useParams();
  const {
    projectAssetsList,
    getAllProjectAssets,
    submitTicket,
    isTicketSubmitted,
    setIsTicketSubmitted,
    groupedAssets,
  } = useProject();

  //HOOKS
  const deviceType = useDeviceType();

  //STATES
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [files, setFiles] = useState([]);

  //for api connection
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const backendPort = process.env.REACT_APP_BACKEND_PORT;
  let apiUrl = `${backendUrl}`;
  if (backendPort) {
    apiUrl += `:${backendPort}`;
  }

  //for masonry layout columns
  const breakpointColumnsObj = {
    default: 1,
    1199: 1,
    700: 1,
    500: 1,
  };

  useEffect(() => {
    const getCurrentDateTime = () => {
      const currentDate = new Date();
      const formattedDateTime = format(
        currentDate,
        "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
      );
      setCurrentDateTime(formattedDateTime);
    };
    getCurrentDateTime();
    const interval = setInterval(getCurrentDateTime, 1000); // Update current date and time every second
    return () => clearInterval(interval);
  }, []);

  let assetFormList;
  if (projectAssetsList.length === 0) {
    getAllProjectAssets(assetId);
  } else {
    const projectAssetFormList = projectAssetsList
      ?.filter((i) => i._id === assetId)
      .map((item) => {
        return item;
      });
    assetFormList = projectAssetFormList[0];
  }

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const [hasChanged, setHasChanged] = useState(false);
  // Watch all fields to track current values
  const watchedFields = watch();
  const [loading, setLoading] = useState(false);

  const FilesData = {
    dateiname: "inform.png",
    uuid: assetId,
    workspace: "INFLOW",
  };

  useEffect(() => {
    getAllFiles(FilesData);
  }, []);

  useEffect(() => {
    if (isTicketSubmitted) {
      history(buildPath('/pages/home'));
      setIsTicketSubmitted(false);
    }
  }, [isTicketSubmitted]);

  const customizeEntries = assetFormList?.customizeEntries || [];
  const assetProperty =
    assetFormList?.properties.map((properties) => {
      const options = {
        option: customizeEntries.filter(
          (customizeEntry) => properties.customizeType === customizeEntry._id
        ),
      };
      return { ...properties, options };
    }) || [];

  // Set initial values when the component mounts
  const initialValues = assetProperty?.reduce((acc, form, index) => {
    acc[`Name${index}`] = ""; // Initialize empty fields
    return acc;
  }, {});

  useEffect(() => {
    // Check if any field value has changed
    const changed = Object.entries(watchedFields).some(
      ([key, value]) => initialValues[key] !== value
    );
    setHasChanged(changed);
  }, [watchedFields]);

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const filePreviews = selectedFiles.map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
    }));

    // Append new files instead of replacing
    setFiles((prevFiles) => [...prevFiles, ...filePreviews]);

    // Reset file input so same file can be reselected
    event.target.value = null;
  };

  // Set this to match your C# Settings.Instance.Workspace value
  const workspace = "INFLOW"; // <-- CHANGE this if needed

  const [selectedFiles, setSelectedFiles] = useState([]); // updated to array
  const [uploadProgress, setUploadProgress] = useState({}); // progress per file
  const [fileUploadMeta, setFileUploadMeta] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadErrors, setUploadErrors] = useState({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  //console.log('uploadErrors', uploadErrors)
  const handleRemoveFile = (event, indexToRemove) => {
    event.stopPropagation(); // Prevents bubbling issues
    event.preventDefault(); // Prevents form submits (if inside a form)

    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );

    // Optionally remove related upload progress/errors/meta
    const removedFile = selectedFiles[indexToRemove];
    if (removedFile) {
      setUploadProgress((prev) => {
        const { [removedFile.name]: _, ...rest } = prev;
        return rest;
      });

      setUploadErrors((prev) => {
        const { [removedFile.name]: _, ...rest } = prev;
        return rest;
      });

      setFileUploadMeta((prev) => {
        const { [removedFile.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Update file input change handler
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    // Filter out duplicates (optional enhancement)
    const existingFileNames = selectedFiles.map((f) => f.name);
    const uniqueNewFiles = newFiles.filter(
      (file) => !existingFileNames.includes(file.name)
    );

    const updatedFiles = [...selectedFiles, ...uniqueNewFiles];

    setSelectedFiles(updatedFiles);

    // Initialize progress and errors for newly added files
    const newProgress = { ...uploadProgress };
    const newErrors = { ...uploadErrors };
    uniqueNewFiles.forEach((file) => {
      newProgress[file.name] = 0;
      newErrors[file.name] = null;
    });

    setUploadProgress(newProgress);
    setUploadErrors(newErrors);
  };

  const handleUploadFile = async (data) => {
    setIsUploadModalOpen(true);
    setIsUploading(true);
    setUploadStatus("Preparing upload...");
    setUploadProgress({});
    setUploadErrors({});
    setFileUploadMeta({});

    const saveFileData = async (ticket_id) => {
      try {
        const newUploadErrors = {};

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];

          try {
            // Resize image if needed (max 800x800 px)
            let resizedFile = file;
            if (file.type.startsWith("image/")) {
              const image = new Image();
              image.src = URL.createObjectURL(file);
              await new Promise((resolve) => {
                image.onload = resolve;
              });

              const canvas = document.createElement("canvas");
              const maxWidth = 800;
              const maxHeight = 800;

              let width = image.width;
              let height = image.height;

              if (width > maxWidth || height > maxHeight) {
                const scale = Math.min(maxWidth / width, maxHeight / height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
              }

              canvas.width = width;
              canvas.height = height;

              const ctx = canvas.getContext("2d");
              ctx.drawImage(image, 0, 0, width, height);

              const resizedBlob = await pica().toBlob(canvas, {
                quality: 3,
                unsharpAmount: 80,
              });
              resizedFile = new File([resizedBlob], file.name, {
                type: file.type,
              });
            }

            setUploadStatus(`Uploading ${file.name}...`);
            const formData = new FormData();
            formData.append("file", resizedFile, file.name);
            formData.append("uuid", ticket_id);
            formData.append("workspace", workspace);

            const startTime = Date.now();

            await axios.post(`${apiUrl}/api/file-upload`, formData, {
              headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "multipart/form-data",
              },
              onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                const percentCompleted = Math.round((loaded * 100) / total);
                const elapsedTime = (Date.now() - startTime) / 1000;
                const speed = loaded / elapsedTime;
                const timeRemainingSec =
                  speed > 0 ? Math.round((total - loaded) / speed) : null;

                setUploadProgress((prev) => ({
                  ...prev,
                  [file.name]: percentCompleted,
                }));

                setFileUploadMeta((prev) => ({
                  ...prev,
                  [file.name]: {
                    timeRemainingSec,
                    speed,
                  },
                }));
              },
            });

            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: 100,
            }));

            setFileUploadMeta((prev) => ({
              ...prev,
              [file.name]: {
                ...prev[file.name],
                timeRemainingSec: 0,
              },
            }));

            //console.log(`✅ Uploaded: ${file.name}`);
          } catch (err) {
            console.error(`❌ Failed to process ${file.name}`, err);

            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: -1,
            }));

            const errorMsg =
              err.response?.data?.detail ||
              err.response?.data?.error ||
              err.message ||
              "Upload failed";
            newUploadErrors[file.name] = errorMsg;

            setFileUploadMeta((prev) => ({
              ...prev,
              [file.name]: {
                ...prev[file.name],
                timeRemainingSec: null,
              },
            }));
          }
        }

        setIsUploading(false);
        setUploadErrors(newUploadErrors);

        if (Object.keys(newUploadErrors).length === 0) {
          toast.success("Ticket submitted successfully!", { autoClose: 1000 });
          setIsTicketSubmitted(true);
          setSelectedFiles([]);
        } else {
          setUploadStatus("Some files failed to upload.");
          toast.error(
            "Some files failed to upload. Please fix the errors before submitting.",
            {
              autoClose: 1500,
            }
          );
        }
      } catch (metaApiError) {
        console.error("❌ Upload process failed:", metaApiError);
        setIsUploading(false);
        setUploadStatus("Upload failed during processing stage.");
        toast.error("Upload failed during file processing.", {
          autoClose: 2000,
        });
      }
    };

    const today = new Date(); // Get the current date
    const formattedDate = moment(today).format("YYYY-MM-DDTHH:mm:ss[Z]");

    // Construct an array of form field objects
    const propertiesData = assetFormList?.properties.map((form, index) => {
      if (form.mode === 1) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueBool: ["true", "yes", "1"].includes(
            data[`Name${index}`]?.toLowerCase()
          ),
        };
      } else if (form.mode === 2) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueInt: data[`Name${index}`],
        };
      } else if (form.mode === 3) {
        const dateValue = data[`Name${index}`];
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: dateValue ? `${dateValue}T00:00:00Z` : null,
        };
      } else if (form.mode === 4) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueString: data[`Name${index}`],
        };
      } else if (form.mode === 5) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueDecimal: data[`Name${index}`],
        };
      } else if (form.mode === 7) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueString: data[`Name${index}`],
        };
      } else {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueTimeStamp: data[`Name${index}`],
        };
      }
    });

    const ticketData = {
      data: {
        chatRoomThema: [
          {
            title: "allgemein",
            description: "",
            createdOnDate: currentDateTime,
            changedOnDate: currentDateTime,
          },
        ],
        parentId: parentId,
        project_id: projectId,
        typeId: assetId,
        name: data.subject,
        description: "",
        properties: propertiesData,
      },
    };

    try {
      setLoading(true);

      if (authToken) {
        const response = await axios.post(`${apiUrl}/api/submit-ticket`, {
          token: authToken,
          ticketData: ticketData,
        });

        if (response.data.success) {
          const ticketId = response?.data?.data?.result?._id; // Extract ticket ID safely

          if (ticketId) {
            if (selectedFiles.length > 0) {
              await saveFileData(ticketId); // saveFileData setzt isTicketSubmitted nach erfolgreichem Upload
            } else {
              toast.success(response.data.message, { autoClose: 1000 });
              setIsTicketSubmitted(true);
            }
          }
        } else {
          toast.error(response.data.message || "Something went wrong.", {
            autoClose: 1000,
          });
        }
      }
    } catch (error) {
      toast.error("Something went wrong.", { autoClose: 1000 });
    } finally {
      setLoading(false);
    }

    // try {
    //   setLoading(true);

    //   if (authToken) {
    //     const response = await axios.post(`${apiUrl}/api/submit-ticket`, {
    //       token: authToken,
    //       ticketData: ticketData,
    //     });

    //     if (response.data.success) {
    //       const ticketId = response?.data?.data?.result?._id;// Safely extract _id
    //       console.log('ticketId', ticketId)
    //       if (ticketId) {
    //         await saveFileData(ticketId);
    //       }
    //     } else {
    //       toast.error(response.data.message || "Something went wrong.", {
    //         autoClose: 1000,
    //       });
    //     }
    //   }
    // } catch (error) {
    //   toast.error("Something went wrong.", { autoClose: 1000 });
    // } finally {
    //   setLoading(false);
    // }
  };

  const submitTicketForm = async (data) => {
    const today = new Date(); // Get the current date
    const formattedDate = moment(today).format("YYYY-MM-DDTHH:mm:ss[Z]");

    // Construct an array of form field objects
    const propertiesData = assetFormList?.properties.map((form, index) => {
      if (form.mode === 1) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueBool: ["true", "yes", "1"].includes(
            data[`Name${index}`]?.toLowerCase()
          ),
        };
      } else if (form.mode === 2) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueInt: data[`Name${index}`],
        };
      } else if (form.mode === 3) {
        const dateValue = data[`Name${index}`];
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: dateValue ? `${dateValue}T00:00:00Z` : null,
        };
      } else if (form.mode === 4) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueString: data[`Name${index}`],
        };
      } else if (form.mode === 5) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueDecimal: data[`Name${index}`],
        };
      } else if (form.mode === 7) {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueString: data[`Name${index}`],
        };
      } else {
        return {
          propertyTypeEntryId: form._id,
          typeId: assetId,
          valueDateTime: formattedDate,
          valueTimeStamp: data[`Name${index}`],
        };
      }
    });

    const ticketData = {
      data: {
        chatRoomThema: [
          {
            title: "allgemein",
            description: "",
            createdOnDate: currentDateTime,
            changedOnDate: currentDateTime,
          },
        ],
        parentId: parentId,
        project_id: projectId,
        typeId: assetId,
        name: data.subject,
        description: "",
        properties: propertiesData,
      },
    };

    // console.log("ticketData", ticketData);

    // try {
    //   setLoading(true);
    //   if (authToken) {
    //     const response = await axios.post((apiUrl += '/api/submit-ticket'), {
    //       token: authToken,
    //       ticketData: ticketData,
    //     })
    //     if (response.data.success) {
    // toast.success(response.data.message, { autoClose: 1000 });
    // setIsTicketSubmitted(true);
    //     } else {
    //       toast.error(response?.data?.message || 'Something went wrong.', { autoClose: 1000 });
    //     }
    //   }
    // } catch (err) {
    //   toast.error('Something went wrong.', { autoClose: 1000 });
    // } finally {
    //   setLoading(false);
    // }
    // toast.success("Ticket submitted successfully!", {
    //   autoClose: 1500,
    // });
    await submitTicket(ticketData, setLoading);
  };

  //console.log('projectAssetsList', projectAssetsList)

  return (
    <Fragment>
      {/* <ToastContainer autoClose={1000}/> */}
      {/* <Breadcrumbs mainTitle="Concern" parent="Pages" title="Concern" /> */}
      <Container fluid={true}>
        {/* {selectedFiles.length > 0 && (
          <FileUploadModal
            isOpen={isUploadModalOpen}
            uploadProgress={uploadProgress}
            selectedFiles={selectedFiles}
            uploadStatus={uploadStatus}
          />
        )} */}

        <Row>
          <Col sm="12" md="10" className="offset-md-1">
            <Row style={{ marginTop: 30 }}>
              <Col
                sm="12"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {projectAssetsList.length === 0 ? (
                  <ConcernSkeleton />
                ) : (
                  <Card style={{ width: 800 }}>
                    <CardHeader>
                      <H4>
                        {category} - {assetName}
                      </H4>
                      {/* <div>{`${format(new Date(), 'yy-MM-dd')}_${user?.username}`}</div> */}
                    </CardHeader>

                    <CardBody>
                      <Form
                        className="needs-validation"
                        noValidate=""
                        //onSubmit={handleSubmit(submitTicketForm)}
                        onSubmit={handleSubmit(handleUploadFile)}
                      >
                        <Row style={{ marginBottom: 50 }}>
                          <Col md="12" style={{ display: "none" }}>
                            <div className="form-floating mb-3">
                              <input
                                className="form-control"
                                id="validationCustom01"
                                readOnly
                                type="text"
                                name={`subject`}
                                {...register("subject", { required: true })}
                                value={`${format(new Date(), "yy-MM-dd")}_${
                                  user?.username
                                }`}
                              />
                              <label htmlFor="floatingInput22">
                                <span style={{ color: "#F07B05" }}>*</span>
                                Subject
                              </label>
                              <span
                                className="d-block"
                                style={{ color: "#F07B05" }}
                              >
                                {errors[`subject`] && "This field is required"}
                              </span>
                            </div>
                          </Col>
                          <Col>
                            <Masonry
                              breakpointCols={breakpointColumnsObj}
                              className="my-masonry-grid masonry-with-dec my-gallery gallery-with-description row grid"
                              columnClassName="col-xl-12 col-sm-12 col-md-12 grid-item"
                            >
                              {assetProperty.map((form, index) => (
                                <div
                                  key={index}
                                  className="form-group mb-3"
                                  style={{ position: "relative" }}
                                >
                                  {/* Input Field Based on Form Mode */}
                                  {form.mode === 2 ? (
                                    <input
                                      className="form-control"
                                      id={`validationCustom01-${index}`}
                                      type="number"
                                      name={`Name${index}`}
                                      {...register(`Name${index}`, {
                                        required: form.mandatory,
                                      })}
                                      style={{
                                        minHeight: "40px",
                                        paddingTop:
                                          "calc(24px + var(--dynamic-label-height, 0px))", // Adjust height dynamically
                                        transition:
                                          "padding-top 0.2s ease-in-out",
                                      }}
                                    />
                                  ) : form.mode === 3 ? (
                                    <input
                                      className="form-control"
                                      id={`validationCustom01-${index}`}
                                      type="date"
                                      name={`Name${index}`}
                                      {...register(`Name${index}`, {
                                        required: form.mandatory,
                                      })}
                                      style={{
                                        minHeight: "40px",
                                        paddingTop:
                                          "calc(24px + var(--dynamic-label-height, 0px))",
                                        transition:
                                          "padding-top 0.2s ease-in-out",
                                      }}
                                    />
                                  ) : form.mode === 4 &&
                                    form.customizeType === "TEXT" ? (
                                    <input
                                      className={`form-control ${
                                        theme === "dark-only"
                                          ? "dark-only"
                                          : "light-only"
                                      }`}
                                      id={`validationCustom01-${index}`}
                                      type="text"
                                      name={`Name${index}`}
                                      {...register(`Name${index}`, {
                                        required: form.mandatory,
                                      })}
                                      style={{
                                        minHeight: "40px",
                                        paddingTop:
                                          "calc(24px + var(--dynamic-label-height, 0px))",
                                        transition:
                                          "padding-top 0.2s ease-in-out",
                                      }}
                                    />
                                  ) : form.mode === 4 &&
                                    form.customizeType === "TEXT2" ? (
                                    <textarea
                                      className={`form-control textarea-custom-scrollbar ${
                                        theme === "dark-only"
                                          ? "dark-only"
                                          : "light-only"
                                      }`}
                                      id={`validationCustom01-${index}`}
                                      style={{
                                        height: "auto",
                                        minHeight: "80px",
                                        //maxHeight: "300px",
                                        paddingTop:
                                          "calc(24px + var(--dynamic-label-height, 0px))",
                                        transition:
                                          "padding-top 0.2s ease-in-out",
                                      }}
                                      name={`Name${index}`}
                                      {...register(`Name${index}`, {
                                        required: form.mandatory,
                                      })}
                                      onInput={(e) => {
                                        e.target.style.height = "auto";
                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                      }}
                                    ></textarea>
                                  ) : form.mode === 5 ? (
                                    <input
                                      className={`form-control ${
                                        theme === "dark-only"
                                          ? "dark-only"
                                          : "light-only"
                                      }`}
                                      id={`validationCustom01-${index}`}
                                      type="number"
                                      name={`Name${index}`}
                                      {...register(`Name${index}`, {
                                        required: form.mandatory,
                                      })}
                                      style={{
                                        minHeight: "40px",
                                        paddingTop:
                                          "calc(24px + var(--dynamic-label-height, 0px))",
                                        transition:
                                          "padding-top 0.2s ease-in-out",
                                      }}
                                    />
                                  ) : form.mode === 7 ? (
                                    <select
                                      className="form-select form-control"
                                      id={`validationCustom01-${index}`}
                                      name={`Name${index}`}
                                      {...register(`Name${index}`, {
                                        required: form.mandatory,
                                      })}
                                      defaultValue=""
                                      style={{
                                        minHeight: "40px",
                                        paddingTop:
                                          "calc(24px + var(--dynamic-label-height, 0px))",
                                        transition:
                                          "padding-top 0.2s ease-in-out",
                                      }}
                                    >
                                      <option value=""></option>
                                      {form.options.option[0].predefiniedProperties.map(
                                        (property, i) => (
                                          <option
                                            key={i}
                                            value={property.value}
                                          >
                                            {property.value}
                                          </option>
                                        )
                                      )}
                                    </select>
                                  ) : (
                                    <div className="radio-container form-control">
                                      <label
                                        className="custom-radio-label w-100"
                                        htmlFor={`Name${index}`}
                                        style={{
                                          color:
                                            theme === "dark-only"
                                              ? "#ffffff"
                                              : "#2f2f3b",
                                          fontSize: 12,
                                        }}
                                      >
                                        {form.mandatory === true && (
                                          <span style={{ color: "#F07B05" }}>
                                            *
                                          </span>
                                        )}
                                        {form.name}
                                      </label>

                                      <div
                                        className="radio-options"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div
                                          className="radio-option"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <input
                                            className="custom-radio"
                                            id={`no-${index}`}
                                            type="radio"
                                            name={`Name${index}`}
                                            value={false}
                                            {...register(`Name${index}`, {
                                              required: true,
                                            })}
                                            style={{
                                              accentColor: "#002878",
                                            }}
                                          />
                                          <label
                                            className="custom-radio-label"
                                            htmlFor={`no-${index}`}
                                            style={{ marginLeft: "8px" }}
                                          >
                                            {t("No")}
                                          </label>
                                        </div>

                                        <div
                                          className="radio-option"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginRight: "15px",
                                          }}
                                        >
                                          <input
                                            className="custom-radio"
                                            id={`yes-${index}`}
                                            type="radio"
                                            name={`Name${index}`}
                                            value={true}
                                            {...register(`Name${index}`, {
                                              required: true,
                                            })}
                                            style={{
                                              accentColor: "#002878",
                                            }}
                                          />
                                          <label
                                            className="custom-radio-label"
                                            htmlFor={`yes-${index}`}
                                            style={{ marginLeft: "8px" }}
                                          >
                                            {t("Yes")}
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Label (Relative to Input) */}
                                  {form.mode !== 1 && (
                                    <label
                                      htmlFor={`validationCustom01-${index}`}
                                      className="form-label"
                                      style={{
                                        position: "absolute",
                                        top: "8px",
                                        left: "12px",
                                        fontSize: "12px",
                                        maxWidth: "calc(100% - 24px)", // Prevent overflow
                                        wordWrap: "break-word",
                                        transition: "top 0.2s ease-in-out",
                                        color:
                                          theme === "dark-only" ? "white" : "",
                                      }}
                                      ref={(el) => {
                                        if (el) {
                                          const labelHeight = el.offsetHeight;
                                          el.parentElement.style.setProperty(
                                            "--dynamic-label-height",
                                            `${labelHeight}px`
                                          );
                                        }
                                      }}
                                    >
                                      {form.mandatory && (
                                        <span style={{ color: "#F07B05" }}>
                                          *
                                        </span>
                                      )}{" "}
                                      {form.name}
                                    </label>
                                  )}

                                  {/* Error Message */}
                                  <span
                                    className="d-block"
                                    style={{
                                      color: "#F07B05",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {errors[`Name${index}`] &&
                                      (form.mode === 1
                                        ? "Please choose one option and cannot submit before"
                                        : t("This field is required"))}
                                  </span>
                                </div>

                                // </Col>
                              ))}
                            </Masonry>
                          </Col>
                          <Col md="12">
                            <div style={{ maxWidth: "100%", marginTop: 30 }}>
                              <h6
                                style={{
                                  color:
                                    theme === "dark-only" ? "#ccc" : "#555",
                                }}
                              >
                                {t(`Upload File(s)`)}
                              </h6>

                              <FileUploadInput
                                theme={theme}
                                selectedFiles={selectedFiles}
                                handleFileChange={handleFileChange}
                              />

                              <div style={{ marginTop: "15px" }}>
                                {selectedFiles.map((file, index) => {
                                  const progress =
                                    uploadProgress[file.name] ?? 0;
                                  const errorMsg = uploadErrors[file.name];
                                  const meta = fileUploadMeta[file.name] || {};
                                  const remainingTime = meta.timeRemainingSec;

                                  const formatTime = (seconds) => {
                                    if (seconds == null) return null;
                                    if (seconds <= 0) return "Done";
                                    if (seconds < 60) return `${seconds}s`;
                                    const mins = Math.floor(seconds / 60);
                                    const secs = seconds % 60;
                                    return `${mins}m ${secs}s`;
                                  };

                                  return (
                                    <Fragment key={index}>
                                      <FileUploadCard
                                        key={index}
                                        file={file}
                                        index={index}
                                        theme={theme}
                                        progress={progress}
                                        remainingTime={remainingTime}
                                        errorMsg={errorMsg}
                                        handleRemoveFile={handleRemoveFile}
                                        formatFileSize={formatFileSize}
                                        formatTime={formatTime}
                                        getFileIcon={getFileIcon}
                                        setUploadStatus={setUploadStatus}
                                      />
                                    </Fragment>
                                  );
                                })}
                              </div>

                              {selectedFiles.length > 0 && (
                                <p
                                  style={{
                                    color:
                                      theme === "dark-only" ? "#ccc" : "#555",
                                  }}
                                >
                                  {uploadStatus}
                                </p>
                              )}

                              {isUploading && (
                                <div
                                  style={{
                                    width: 100,
                                    height: 50,
                                    marginTop: -50,
                                    marginLeft: -15,
                                  }}
                                >
                                  <Lottie animationData={sending} />
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                        <Btn
                          attrBtn={{
                            color: "primary",
                            disabled: loading || isUploading,
                          }}
                          className="text-end"
                        >
                          {loading ? `${t("Submitting")}...` : t("Submit")}
                        </Btn>
                      </Form>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ConcernPage;
